/**
 * Device-local calendar-sync bookkeeping (#1715).
 *
 * This store is deliberately device-scoped infrastructure metadata, not
 * feature data: it records which writable device calendar the patient chose
 * and which native event ids Louise created there, so a re-sync can update or
 * remove its own events instead of duplicating them. Native calendar event
 * ids are meaningless on any other device, so a server copy could never be
 * authoritative — the same reasoning that keeps push tokens device-resolved.
 * The care schedule itself always comes from the API; nothing here is ever a
 * data source for what appointments or medications exist.
 */

import { useEffect, useSyncExternalStore } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { SyncedEventRecord } from "./calendar-export";

const CALENDAR_SYNC_KEY_PREFIX = "louise.calendar_sync.";
const STORE_VERSION = 1;

export type CalendarSyncState = {
  version: typeof STORE_VERSION;
  calendarId: string;
  /** Display name captured at selection time for the settings UI. */
  calendarTitle: string;
  events: Record<string, SyncedEventRecord>;
  /** RFC 3339 instant of the last completed sync attempt. */
  lastSyncedAt: string | null;
  /** Occurrences the last sync failed to write; drives the retry state. */
  lastSyncFailedCount: number;
};

function storageKey(patientId: string): string {
  return `${CALENDAR_SYNC_KEY_PREFIX}${patientId}`;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let currentPatientId: string | undefined;
let state: CalendarSyncState | null = null;
let isLoading = true;

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getStateSnapshot(): CalendarSyncState | null {
  return state;
}

function getLoadingSnapshot(): boolean {
  return isLoading;
}

function parseState(raw: string | null): CalendarSyncState | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      "version" in parsed &&
      parsed.version === STORE_VERSION &&
      "calendarId" in parsed &&
      typeof parsed.calendarId === "string"
    ) {
      return parsed as CalendarSyncState;
    }
  } catch {
    // Corrupt or future-versioned payloads reset to disconnected; the next
    // setup rebuilds them from the device calendar store.
  }
  return null;
}

async function loadState(patientId: string) {
  isLoading = true;
  notifyListeners();
  try {
    state = parseState(await AsyncStorage.getItem(storageKey(patientId)));
  } catch {
    state = null;
  }
  isLoading = false;
  notifyListeners();
}

export async function writeCalendarSyncState(
  patientId: string,
  next: Omit<CalendarSyncState, "version">,
): Promise<CalendarSyncState> {
  const value: CalendarSyncState = { version: STORE_VERSION, ...next };
  if (patientId === currentPatientId) {
    state = value;
    notifyListeners();
  }
  await AsyncStorage.setItem(storageKey(patientId), JSON.stringify(value));
  return value;
}

export async function clearCalendarSyncState(patientId: string) {
  if (patientId === currentPatientId) {
    state = null;
    notifyListeners();
  }
  await AsyncStorage.removeItem(storageKey(patientId));
}

/** Read without subscribing — used inside the sync mutation. */
export async function readCalendarSyncState(
  patientId: string,
): Promise<CalendarSyncState | null> {
  if (patientId === currentPatientId && !isLoading) return state;
  try {
    return parseState(await AsyncStorage.getItem(storageKey(patientId)));
  } catch {
    return null;
  }
}

/**
 * Subscribe to the current patient's calendar-sync state. Returns null while
 * disconnected and `isLoading` during the initial device read so the UI can
 * separate "not set up" from "still loading".
 */
export function useCalendarSyncState(patientId: string | undefined) {
  useEffect(() => {
    if (!patientId) {
      currentPatientId = undefined;
      state = null;
      isLoading = false;
      notifyListeners();
      return;
    }
    if (patientId !== currentPatientId) {
      currentPatientId = patientId;
      void loadState(patientId);
    }
  }, [patientId]);

  const currentState = useSyncExternalStore(
    subscribe,
    getStateSnapshot,
    getStateSnapshot,
  );
  const loading = useSyncExternalStore(
    subscribe,
    getLoadingSnapshot,
    getLoadingSnapshot,
  );

  return { state: currentState, isLoading: loading };
}
